import React, { useEffect, useState } from 'react';
import { Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import validator from 'validator';
import config from '../../config/Constant';
import { getRequest, postRequest } from '../../helpers/apiHandler';
import { formatCurrency, formatWithoutCurrency, sortByDate } from '../../helpers/helperFunction';
import CloseIcon from '../../layouts/SVG/CloseIcon';
import { TransferIcon } from '../../layouts/SVG/ManagerIcons';
import { reloadTopbar } from '../../store/actions/reduxAction';
import CustomAlert from '../CustomAlert';
import AccountCard from './AccountCard';
import './_managerTransferCredits.scss';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const LinkedAccount = () => {
  const { access_token, user } = useSelector((state) => state.auth);
  const [loadingSend, setLoadingSend] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [linkedAccountData, setLinkedAccountData] = useState([]);
  const [memberNamesList, setMemberNamesList] = useState([]);

  const [senderUserId, setSenderUserId] = useState(user.id);
  const [senderUserName, setSenderUserName] = useState('');
  const [receiverUserId, setReceiverUserId] = useState('');
  const [receiverUserName, setReceiverUserName] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditState, setCreditState] = useState(false);

  const [transferError, setTransferError] = useState({ error: false, message: '' });
  const [creditError, setCreditError] = useState({ error: false, message: '' });
  const [availableBalance, setAvailableBalance] = useState(0);
  const dispatch = useDispatch();

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const getLinkedAccountCards = () => {
    const data = {
      headers: { Authorization: access_token.token },
    };
    getRequest('/admin/credit-transfer-history', data, (res) => {
      if (res.success) {
        const { data } = res;
        setLinkedAccountData(data.linked_accounts_cards);
        setMemberNamesList(data.member_name_list);
      }
    });
  };

  const handleTransferCredit = (evt) => {
    evt.preventDefault();
    const form = evt.currentTarget;

    if (form.checkValidity() === false) {
      evt.stopPropagation();
    }

    if (!validator.isNumeric(creditAmount) || creditAmount <= 0) {
      setCreditError({
        error: true,
        message: 'Please enter a valid credit amount.',
      });
      return;
    }

    if (senderUserId === receiverUserId) {
      setTransferError({
        error: true,
        message: 'Credit transfer to own account not allowed',
      });
      return;
    }

    setLoadingSend(true);

    const data = {
      sender_user_id: senderUserId,
      receiver_user_id: receiverUserId,
      credit_amount: creditAmount,
    };

    postRequest('/admin/transfer-credit', data, access_token.token, (res) => {
      if (res.status_code === 401) {
        window.location.href = '/login';
      }
      if (res.status_code === 200) {
        getLinkedAccountCards();
        setAlertState({
          show: true,
          message: 'Credits have been transferred successfully',
          variant: 'success',
        });
        handleTransferModalClose();
        setLoadingSend(false);
      } else {
        setAlertState({
          show: true,
          message: res.message,
          variant: 'danger',
        });
        if (res.code === 12065) {
          // const { cost } = res.data;
          // setShowOutOfCreditModal(true);
          // setAmountReqForCampaign(cost);
          setTransferError({
            error: true,
            message: 'Insufficient credits to transfer',
          });
          setAlertState({
            show: true,
            message: 'Sorry! There was an error in transferring the credits',
            variant: 'danger',
          });
        }
        setLoadingSend(false);
      }
      dispatch(reloadTopbar());
    });
  };

  useEffect(() => {
    getLinkedAccountCards();
  }, []);

  const handleTransferModalOpen = () => {
    setTransferModal(true);
  };

  const handleTransferModalClose = () => {
    setTransferModal(false);
    setAvailableBalance(0);
  };

  const handleSenderChange = (event) => {
    const name = event.nativeEvent.target[event.nativeEvent.target.selectedIndex].text;
    setSenderUserId(event.target.value);
    setSenderUserName(name);

    if (name !== selectAccountPlaceholder) {
      event.target.classList.add('selected');
    } else {
      event.target.classList.remove('selected');
    }

    const member = memberNamesList.find((member) => member.user_id == event.target.value);

    if (member) {
      setAvailableBalance(member.available_balance);
    }

    setCreditError({ error: false, message: '' });
    if (event.target.value === receiverUserId) {
      setTransferError({
        error: true,
        message: 'Credit transfer to own account not allowed',
      });
    } else {
      setTransferError({ error: false, message: '' });
    }
  };

  const handleReceiverChange = (event) => {
    const name = event.nativeEvent.target[event.nativeEvent.target.selectedIndex].text;
    setReceiverUserId(event.target.value);
    setReceiverUserName(name);

    if (name !== selectAccountPlaceholder) {
      event.target.classList.add('selected');
    } else {
      event.target.classList.remove('selected');
    }

    setCreditError({ error: false, message: '' });
    if (senderUserId === event.target.value) {
      setTransferError({
        error: true,
        message: 'Credit transfer to own account not allowed',
      });
    } else {
      setTransferError({ error: false, message: '' });
    }
  };

  const handleAmountChange = (event) => {
    setCreditAmount(event.target.value);
    setCreditError({ error: false, message: '' });
  };

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  const columns = [
    {
      name: 'Date',
      sortable: true,
      selector: (row) => row.date,
      sortFunction: sortByDate('date'),
    },
    {
      name: 'Amount',
      sortable: true,
      selector: (row) =>
        white_label_perkeso
          ? formatWithoutCurrency(row.amount, 2, 2)
          : formatCurrency(user?.currency?.iso3, row.amount, 2),
    },
    {
      name: 'From Account',
      sortable: true,
      selector: (row) => row.from_account,
    },
    {
      name: 'To Account',
      sortable: true,
      selector: (row) => row.to_account,
    },
  ];

  const selectAccountPlaceholder = 'Select Account';

  const formatUserBalance = (user) => {
    return white_label_perkeso
      ? formatWithoutCurrency(user?.available_balance, 0)
      : formatCurrency(
          user?.currency?.iso3,
          user?.available_balance ? user?.available_balance : 0,
          0
        );
  };

  return (
    <div className="CreditManagement">
      <div className="w-100 p-4 bg-white">
        <div className="LinkedAccount">
          <CustomAlert
            message={alertState.message}
            show={alertState.show}
            variant={alertState.variant}
            closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
          />
          <div className="account-heading-wrapper" style={{ marginBottom: '16px' }}>
            <h4 style={{ fontWeight: 600, fontSize: '20px', lineHeight: '30px' }}>
              Credit Management
            </h4>
          </div>
          <div className="account-heading-wrapper">
            <div className="container-left">
              <div className="account-heading-item">
                <span style={{ color: '#344054' }}>{user?.name ? user?.name : '-'}</span>
                <span style={{ fontWeight: 400, lineHeight: '21px' }}>ORGANIZATION</span>
              </div>
              <div className="account-heading-item">
                <span style={{ color: '#344054' }}>{formatUserBalance(user)}</span>
                <span style={{ fontWeight: 400, lineHeight: '21px' }}>CREDIT BALANCE</span>
              </div>
            </div>
            <div className="container-right">
              <Button variant="primary" onClick={handleTransferModalOpen} style={{ minWidth: 160 }}>
                <TransferIcon />
                <span style={{ marginLeft: '8px' }}>Transfer Credit</span>
              </Button>
            </div>
          </div>
          <div className="account-card-wrapper">
            {linkedAccountData.map((account, index) => (
              <AccountCard
                key={index}
                role={account.role}
                name={account.name}
                totalCredits={account.total_credits}
                availableCredits={account.available_credits}
              />
            ))}
          </div>
          <Modal
            show={transferModal}
            className="modal-delete"
            animation={true}
            onHide={handleTransferModalClose}
            centered
          >
            <Modal.Body>
              <CloseIcon handleModalClose={handleTransferModalClose} />
              <Modal.Title as="h4" style={{ margin: '16px 0 8px' }}>
                Transfer Credit
              </Modal.Title>
              <Form noValidate onSubmit={handleTransferCredit}>
                <Form.Group>
                  <strong>Credit Balance: </strong>{' '}
                  {white_label_perkeso
                    ? formatWithoutCurrency(availableBalance, 0, 0)
                    : formatCurrency(user?.currency?.iso3, availableBalance, 2)}
                </Form.Group>
                <Form.Group>
                  <Form.Label>From Account</Form.Label>
                  <Form.Control
                    as="select"
                    onChange={handleSenderChange}
                    className="transfer-select"
                  >
                    <option value="">{selectAccountPlaceholder}</option>
                    {memberNamesList.map((data, index) => {
                      return (
                        <option key={index} value={data.user_id}>
                          {data.name}
                        </option>
                      );
                    })}
                  </Form.Control>
                </Form.Group>
                <Form.Group>
                  <Form.Label>To Account</Form.Label>
                  <Form.Control
                    as="select"
                    onChange={handleReceiverChange}
                    className="transfer-select"
                  >
                    <option value="">{selectAccountPlaceholder}</option>
                    {memberNamesList.map((data, index) => {
                      return (
                        <option key={index} value={data.user_id}>
                          {data.name}
                        </option>
                      );
                    })}
                  </Form.Control>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Credits</Form.Label>
                  <InputGroup hasValidation className={creditError.error ? 'is-invalid' : ''}>
                    <Form.Control
                      type="number"
                      onChange={handleAmountChange}
                      onFocus={() => setCreditState(true)}
                      onBlur={() => {
                        setCreditState(false);
                        if (senderUserName !== receiverUserName) {
                          setTransferError({ error: false, message: '' });
                        }
                      }}
                      isInvalid={creditError.error}
                      placeholder="Enter Amount"
                      className="modal-transfer-amount"
                    />
                    <Form.Control.Feedback type="invalid">
                      {creditError.message}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                {creditAmount !== '' && !transferError.error && (
                  <Form.Group className="notice-success">
                    Confirm transfer of{' '}
                    <strong>
                      {white_label_perkeso
                        ? formatWithoutCurrency(creditAmount, 0, 0)
                        : formatCurrency(user?.currency?.iso3, creditAmount, 2)}
                    </strong>{' '}
                    credits from <strong>{senderUserName}</strong> to{' '}
                    <strong>{receiverUserName}</strong>
                  </Form.Group>
                )}
                {transferError.error && (
                  <Form.Group className="notice-danger">{transferError.message}</Form.Group>
                )}
                <Form.Group className="d-flex justify-content-between">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loadingSend}
                    style={{ minWidth: 160 }}
                  >
                    {loadingSend && <Spinner animation="border" size="sm" className="mr-2" />}
                    Transfer Credit
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    style={{ minWidth: 160 }}
                    onClick={handleTransferModalClose}
                  >
                    Cancel
                  </Button>
                </Form.Group>
              </Form>
            </Modal.Body>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default LinkedAccount;
